/**
 * AuthStoreHydrator
 *
 * Single place for the auth bootstrap sequence.
 * Mounted once in App.jsx (inside Router, wrapping the rest of the tree).
 *
 * ## Auth responsibilities
 *
 * 1. On mount: read storage → set token / policy_pending / unauthenticated.
 * 2. React to authToken changes: decode JWT, fetch user data.
 * 3. React to userData changes: set authBootstrapStatus to authenticated.
 * 4. Wire useInactivityLogout (10 min inactivity timeout).
 *
 * ## Preload responsibilities (after authenticated, one-shot latches)
 *
 * | Phase | Function | Blocks UI? |
 * | --- | --- | --- |
 * | Mount (cold refresh) | `preloadNamespacesForRoute(current path)` | No — parallel with JWT fetch |
 * | Authenticated | `preloadNamespacesForRoute` + `preloadRemainingNamespaces` | No — background i18n |
 * | Authenticated | `preloadPostLoginModuleChunks` | No — background JS queue |
 *
 * @see docs/Auth/AuthStoreHydrator.md
 * @see docs/runtime-preload.md
 */
import { useEffect, useRef, useCallback } from "react";
import { getUserData } from "api/services/usersServices";
import { logout } from "auth";
import { mapApiUserToAuthUserData } from "auth/userData";
import jwt_decode from "jwt-decode";
import useAuthStore from "stores/useAuthStore";
import useInactivityLogout from "hooks/useInactivityLogout";
import { getRouteVariantSnapshot } from "hooks/useMobileViewport";
import { preloadNamespacesForRoute, preloadRemainingNamespaces } from "i18n";
import { getDefaultAuthenticatedPath } from "modules/appModuleAccess";
import { preloadPostLoginModuleChunks } from "routes/routeChunkLoaders";
import { clearSentryUser, setSentryUser } from "observability/sentry";

// Store access
const store = useAuthStore.getState();

export default function AuthStoreHydrator({ children }) {
  const hasBootstrapped = useRef(false);
  const lastFetchedUserIdRef = useRef(null);
  /** One-shot latch: background i18n warmup after first `authenticated` transition. */
  const hasStartedAuthenticatedNamespaceWarmupRef = useRef(false);
  /** One-shot latch: sequential JS chunk queue after first `authenticated` + userData. */
  const hasStartedAuthenticatedChunkWarmupRef = useRef(false);

  const authToken = useAuthStore((s) => s.authToken);
  const initialAuthToken = useAuthStore((s) => s.initialAuthToken);
  const userData = useAuthStore((s) => s.userData);
  const authBootstrapStatus = useAuthStore((s) => s.authBootstrapStatus);

  useInactivityLogout({
    isAuthenticated: !!authToken,
    logout: () => logout({ setExpired: true }),
  });

  const fetchUserData = useCallback(async (userId, decodedTokenData) => {
    let response;

    try {
      response = await getUserData(userId);
    } catch {
      const idFromToken = decodedTokenData?._id || decodedTokenData?.id;
      return {
        fetchedUserData: {
          ...(idFromToken && { id: idFromToken }),
          ...(decodedTokenData.sub && { email: decodedTokenData.sub }),
          ...(decodedTokenData.role && { role: decodedTokenData.role }),
        },
        initialUserData: null,
      };
    }

    return {
      fetchedUserData: mapApiUserToAuthUserData(response),
      initialUserData: initialAuthToken === authToken ? decodedTokenData : jwt_decode(initialAuthToken),
    };
  }, [initialAuthToken, authToken]);

  // ── Step 1-3: Bootstrap on mount ──
  useEffect(() => {
    if (hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    store.setAuthBootstrapStatus("bootstrapping");

    const token = localStorage.getItem("token");
    const initialToken = localStorage.getItem("initial_token");
    const pendingToken = localStorage.getItem("pending_token");

    if (token || initialToken) {
      // Hard refresh with session — preload current route i18n while JWT/user fetch runs.
      void preloadNamespacesForRoute(window.location.pathname);
    } else if (pendingToken) {
      // Policy gate only needs terms namespace before consent UI renders.
      void preloadNamespacesForRoute("/terms-and-conditions");
    }

    if (token) {
      store.setAuthToken(token);
      if (initialToken) store.setInitialAuthToken(initialToken);
      return;
    }

    if (pendingToken) {
      store.setAuthBootstrapStatus("policy_pending");
      return;
    }

    store.clearSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 4: Token decode and user data fetching ──
  useEffect(() => {
    if (!authToken) {
      lastFetchedUserIdRef.current = null;
      return;
    }

    if (authBootstrapStatus === "policy_pending") return;

    let decodedTokenData;
    try {
      decodedTokenData = jwt_decode(authToken);
    } catch {
      store.clearSession();
      return;
    }

    const userId = decodedTokenData?._id || decodedTokenData?.id;
    if (!userId) {
      store.clearSession();
      return;
    }

    if (lastFetchedUserIdRef.current === userId) return;
    lastFetchedUserIdRef.current = userId;

    fetchUserData(userId, decodedTokenData)
      .then(({ fetchedUserData, initialUserData }) => {
        store.setUserData(fetchedUserData);
        if (initialUserData) store.setInitialUserData(initialUserData);
      })
      .catch((err) => {
        console.error(err);
        const idFromToken = decodedTokenData?._id || decodedTokenData?.id;
        if (idFromToken) {
          store.setUserData({
            id: idFromToken,
            ...(decodedTokenData.sub && { email: decodedTokenData.sub }),
            ...(decodedTokenData.role && { role: decodedTokenData.role }),
          });
        } else {
          store.clearSession();
        }
      });
  }, [authToken, authBootstrapStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 5: Final authentication after userData is set ──
  useEffect(() => {
    if (!userData || authBootstrapStatus === "policy_pending") return;

    if (!userData?.id) {
      store.clearSession();
      return;
    }

    store.setAuthBootstrapStatus("authenticated");
  }, [userData, authBootstrapStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sentry receives only an opaque user ID and role after the full auth
  // bootstrap succeeds. All other states clear context immediately.
  useEffect(() => {
    if (authBootstrapStatus === "authenticated" && userData?.id) {
      setSentryUser(userData);
      return;
    }

    clearSentryUser();
  }, [authBootstrapStatus, userData]);

  useEffect(() => {
    if (authBootstrapStatus !== "authenticated") {
      // Reset warm-up latches on logout so the next session can warm again.
      hasStartedAuthenticatedNamespaceWarmupRef.current = false;
      hasStartedAuthenticatedChunkWarmupRef.current = false;
      return;
    }

    // --- i18n: shell + route gate, then background remaining namespaces ---
    if (!hasStartedAuthenticatedNamespaceWarmupRef.current) {
      hasStartedAuthenticatedNamespaceWarmupRef.current = true;

      void preloadNamespacesForRoute(window.location.pathname).then(() => {
        void preloadRemainingNamespaces();
      });
    }

    // --- JS chunks: access-filtered queue; separate from i18n gate ---
    if (!hasStartedAuthenticatedChunkWarmupRef.current && userData?.id) {
      hasStartedAuthenticatedChunkWarmupRef.current = true;
      const { isMobileViewport } = getRouteVariantSnapshot();
      const currentPathname = typeof window !== "undefined" ? window.location.pathname : undefined;
      // Skip warming the chunk for the page we're already on (or about to land on).
      const preloadLandingPath = currentPathname === "/" || currentPathname === "/login"
        ? getDefaultAuthenticatedPath(userData)
        : currentPathname;

      void preloadPostLoginModuleChunks(userData, {
        variant: isMobileViewport ? "mobile" : "desktop",
        currentPathname: preloadLandingPath,
      });
    }
  }, [authBootstrapStatus, userData]);

  return children;
};
